/**
 * AtipaySheet — aTIProto / Stripe checkout flow sheet.
 *
 * Manages the full real-money payment lifecycle for a single bill package:
 *   idle -> loading -> browser_open -> verifying -> success | error
 *
 * Used inside BuyBillsSheet when the user taps "Payer avec Stripe".
 * On success, calls onSuccess(bills) so the parent can credit BillsContext.
 */
import {useCallback, useState} from 'react'
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native'
import {LinearGradient} from 'expo-linear-gradient'
import * as WebBrowser from 'expo-web-browser'
import {Feather} from '@expo/vector-icons'

import {DinarSymbol} from '#/screens/Dashboard/components/DinarSymbol'
import {
  type AtipayCreds,
  fulfillPayment,
  initiateCheckout,
} from '#/screens/Wallet/lib/atipayment'

// ── Types ─────────────────────────────────────────────────────────────────────
interface OfferSummary {
  id: string
  bills: number
  priceNum: number
  label: string
  emoji: string
  accentColor: string
}

interface Props {
  offer: OfferSummary
  creds: AtipayCreds
  userId: string
  onSuccess: (bills: number) => void
  onCancel: () => void
}

type Phase = 'idle' | 'creating' | 'browser' | 'verifying' | 'success' | 'error'

// ── Component ─────────────────────────────────────────────────────────────────
export function AtipaySheet({
  offer,
  creds,
  userId,
  onSuccess,
  onCancel,
}: Props) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [cartUri, setCartUri] = useState<string | null>(null)
  const [creditedBills, setCreditedBills] = useState(0)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Step 1: create cart -> open browser
  const handlePay = useCallback(async () => {
    setPhase('creating')
    setErrorMsg(null)
    try {
      const result = await initiateCheckout(creds, offer.id, userId)
      setCartUri(result.cartUri)
      setPhase('browser')

      await WebBrowser.openBrowserAsync(result.checkoutUrl, {
        presentationStyle: WebBrowser.WebBrowserPresentationStyle.PAGE_SHEET,
        controlsColor: offer.accentColor,
        dismissButtonStyle: 'cancel',
      })

      setPhase('verifying')
    } catch (err) {
      setErrorMsg(
        err instanceof Error ? err.message : "Echec de l'initialisation",
      )
      setPhase('error')
    }
  }, [creds, offer, userId])

  // Step 2: verify payment on server -> credit bills
  const handleVerify = useCallback(async () => {
    if (!cartUri) return
    setPhase('verifying')
    setErrorMsg(null)
    try {
      const result = await fulfillPayment(creds, cartUri, offer.id, userId)

      if (result.status === 'completed' || result.alreadyFulfilled) {
        const creditedCount = result.bills > 0 ? result.bills : offer.bills
        setCreditedBills(creditedCount)
        setPhase('success')

        if (!result.alreadyFulfilled) {
          fetch(`/api/wallet/purchase-note`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
              userId,
              bills: creditedCount,
              packageLabel: offer.label,
              amountEur: offer.priceNum,
            }),
          }).catch(() => {
            /* non-critical -- bills are already credited locally */
          })
        }

        setTimeout(() => onSuccess(creditedCount), 1800)
      } else {
        setErrorMsg(
          result.status === 'pending' || result.status === 'authorized'
            ? 'Le paiement est en cours de traitement - reessaie dans quelques secondes.'
            : `Statut du paiement : ${result.status}`,
        )
        setPhase('error')
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Erreur de verification')
      setPhase('error')
    }
  }, [cartUri, creds, offer, userId, onSuccess])

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <View style={s.root}>
      {/* Offer summary */}
      <View style={[s.offerRow, {borderColor: offer.accentColor + '30'}]}>
        <Text style={s.offerEmoji}>{offer.emoji}</Text>
        <View style={{flex: 1}}>
          <Text style={s.offerTitle}>{offer.label}</Text>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 3}}>
            <Text style={[s.offerBills, {color: offer.accentColor}]}>
              {offer.bills} bills ·{' '}
            </Text>
            <DinarSymbol size={11} color={offer.accentColor} />
            <Text style={[s.offerBills, {color: offer.accentColor}]}>
              {offer.priceNum.toFixed(2)}
            </Text>
          </View>
        </View>
        <View style={[s.stripeBadge, {borderColor: offer.accentColor + '40'}]}>
          <Text style={[s.stripeBadgeText, {color: offer.accentColor}]}>
            Stripe
          </Text>
        </View>
      </View>

      {/* Phase content */}
      {phase === 'idle' && (
        <View style={s.section}>
          <Text style={s.hint}>
            Paiement securise via Stripe · Protocole AT Protocol
          </Text>
          <Pressable accessibilityRole="button" onPress={handlePay}>
            <LinearGradient
              colors={[offer.accentColor, offer.accentColor + 'bb']}
              start={{x: 0, y: 0}}
              end={{x: 1, y: 0}}
              style={s.payBtn}>
              <Feather name="credit-card" size={16} color="#fff" />
              <View
                style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
                <Text style={s.payBtnText}>Payer</Text>
                <DinarSymbol size={13} color="#fff" />
                <Text style={s.payBtnText}>{offer.priceNum.toFixed(2)}</Text>
              </View>
            </LinearGradient>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            onPress={onCancel}
            style={s.cancelBtn}>
            <Text style={s.cancelText}>Annuler</Text>
          </Pressable>
        </View>
      )}

      {phase === 'creating' && (
        <View style={s.centeredSection}>
          <ActivityIndicator size="large" color={offer.accentColor} />
          <Text style={s.statusText}>
            Creation de la session de paiement...
          </Text>
        </View>
      )}

      {phase === 'browser' && (
        <View style={s.centeredSection}>
          <Text style={s.bigIcon}>🔓</Text>
          <Text style={s.statusText}>Stripe ouvert dans votre navigateur</Text>
          <Text style={s.subText}>
            Completez le paiement, puis revenez ici.
          </Text>
        </View>
      )}

      {phase === 'verifying' && (
        <View style={s.centeredSection}>
          <ActivityIndicator size="large" color={offer.accentColor} />
          <Text style={s.statusText}>Verification du paiement...</Text>
        </View>
      )}

      {phase === 'success' && (
        <View style={s.centeredSection}>
          <Text style={s.bigIcon}>🎉</Text>
          <Text style={[s.statusText, {color: '#22c55e'}]}>
            Paiement confirme !
          </Text>
          <View
            style={[
              s.billsTag,
              {
                backgroundColor: offer.accentColor + '20',
                borderColor: offer.accentColor + '50',
              },
            ]}>
            <Text style={[s.billsTagText, {color: offer.accentColor}]}>
              +{creditedBills} 💵 bills ajoutes
            </Text>
          </View>
        </View>
      )}

      {phase === 'error' && (
        <View style={s.section}>
          <View style={s.errorBox}>
            <Feather name="alert-circle" size={18} color="#f87171" />
            <Text style={s.errorText}>{errorMsg}</Text>
          </View>
          {cartUri && (
            <Pressable
              accessibilityRole="button"
              onPress={handleVerify}
              style={s.retryBtn}>
              <Text style={s.retryText}>Reessayer la verification</Text>
            </Pressable>
          )}
          <Pressable
            accessibilityRole="button"
            onPress={cartUri ? () => setPhase('verifying') : handlePay}
            style={s.retryBtn}>
            <Text style={s.retryText}>
              {cartUri ? 'Verifier a nouveau' : 'Reessayer le paiement'}
            </Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            onPress={onCancel}
            style={s.cancelBtn}>
            <Text style={s.cancelText}>Annuler</Text>
          </Pressable>
        </View>
      )}

      {/* "Verify payment" CTA shown after browser closes */}
      {phase === 'browser' && (
        <Pressable
          accessibilityRole="button"
          onPress={handleVerify}
          style={{marginTop: 16}}>
          <LinearGradient
            colors={[offer.accentColor, offer.accentColor + 'bb']}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 0}}
            style={s.payBtn}>
            <Feather name="check-circle" size={16} color="#fff" />
            <Text style={s.payBtnText}>Verifier le paiement</Text>
          </LinearGradient>
        </Pressable>
      )}
    </View>
  )
}

// ── Styles (dark-locked — matches BuyBillsSheet premium aesthetic) ─────────────
const s = StyleSheet.create({
  root: {
    paddingHorizontal: 4,
    paddingBottom: 8,
  },
  offerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderWidth: 1,
    borderRadius: 14,
    padding: 14,
    marginBottom: 16,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  offerEmoji: {fontSize: 28},
  offerTitle: {color: '#fff', fontSize: 15, fontWeight: '700'},
  offerBills: {fontSize: 12, fontWeight: '600', marginTop: 2},
  stripeBadge: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  stripeBadgeText: {fontSize: 11, fontWeight: '700'},

  section: {gap: 10},
  centeredSection: {alignItems: 'center', paddingVertical: 20, gap: 12},

  hint: {
    color: '#475569',
    fontSize: 11,
    textAlign: 'center',
    marginBottom: 4,
  },
  payBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 14,
    padding: 15,
  },
  payBtnText: {color: '#fff', fontSize: 15, fontWeight: '700'},

  cancelBtn: {alignItems: 'center', paddingVertical: 10},
  cancelText: {color: '#475569', fontSize: 13},

  bigIcon: {fontSize: 52},
  statusText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
    textAlign: 'center',
  },
  subText: {color: '#64748b', fontSize: 12, textAlign: 'center'},

  billsTag: {
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginTop: 4,
  },
  billsTagText: {fontSize: 15, fontWeight: '800'},

  errorBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: 'rgba(248,113,113,0.1)',
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: 'rgba(248,113,113,0.25)',
  },
  errorText: {color: '#fca5a5', fontSize: 13, flex: 1, lineHeight: 18},

  retryBtn: {
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  retryText: {color: '#94a3b8', fontSize: 13, fontWeight: '600'},
})
